import { Logger } from '~/helpers/logger';
import { useApi } from '~/composables';
import { useContext } from '@nuxtjs/composition-api';
import { IS_USERCOM_ACTIVE, USERCOM_CART_SEGMENT_ID, USERCOM_PRODUCT_SEGMENT_ID } from '..';
import usercomNewsletterGql from '../queries/usercomNewsletter.gql';
import { UseUsercomInterface } from './useUsercom';

export function useUserCom(): UseUsercomInterface {
  const { i18n } = useContext();
  const { query } = useApi();
  const isUserComActive = IS_USERCOM_ACTIVE;
  const userComCartSegmentID = USERCOM_CART_SEGMENT_ID;
  const userComProductSegmentID = USERCOM_PRODUCT_SEGMENT_ID;

  const getKey = () => {
    if (!isUserComActive) {
      return '';
    }
    if (process.env.VSF_NUXT_APP_ENV === 'development') {
      return process.env.VSF_DEV_USERCOM_ID;
    }
    if (document) {
      const word = '__ca__chat';
      const caChatId = document.cookie.match(new RegExp(`${word}\\=([^;]+?);`));
      if (caChatId) {
        return caChatId[1];
      }
    }
    if ((window as any).civchat && (window as any).civchat.userKey) {
      return (window as any).civchat.userKey;
    }
    return '';
  };

  const getNRandomElements = (array: string[], n: number): string[] => {
    // Shuffle array
    const shuffled = array.sort(() => 0.5 - Math.random());

    return shuffled.slice(0, n);
  };

  const getPrediction = async (
    usercomURL: string,
    segmentId: number,
  ) => {
    const recommendedProductSkus: string[] = [];
    if (!isUserComActive) {
      return [];
    }
    try {
      const userKey = getKey();
      if (!userKey) {
        Logger.error('Cannot get userCom key');
        return [];
      }
      const response = await fetch(
        `${usercomURL}/predictions/predict-for-user/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_key: userKey,
            limit: 25,
            extra_data: 0,
            segment_id: segmentId,
          }),
        },
      );
      const data = await response.json();
      if (data && data.recommended_products) {
        data.recommended_products.forEach((recommendedProduct) => {
          const itemGroupIdObj = recommendedProduct.attributes
            && recommendedProduct.attributes.find(
              (attr) => attr.name === 'item_group_id',
            );
          const productSku = itemGroupIdObj?.value
            ? itemGroupIdObj.value
            : recommendedProduct.custom_id;
          if (typeof productSku === 'string') {
            recommendedProductSkus.push(productSku);
          }
        });
      }
    } catch (e) {
      console.log(`Cannot get userCom predictions${e}`);
    }
    const randomProductSkus = getNRandomElements(recommendedProductSkus, 8);
    return [...new Set(randomProductSkus)];
  };

  const queryNewsletter = async (
    email?: string,
    userId: number = 0,
    customerTag: any = 'storyblok_newsletter',
  ) => {
    const dataForUsercom = {
      input: {
        user_id: userId,
        usercom_id: getKey(),
        email,
        customer_tag: customerTag,
      },
    };
    const { data, errors } = await query(usercomNewsletterGql, dataForUsercom);

    const message = ((data as any)?.usercomNewsletter?.error || errors?.length)
      ? i18n.t('Newsletter signup error') as string
      : i18n.t('Subscribed to the newsletter') as string;

    return message;
  };

  return {
    isUserComActive,
    userComCartSegmentID,
    userComProductSegmentID,
    getKey,
    getPrediction,
    queryNewsletter,
  };
}
